import { useEffect, useRef, useState } from "react";
import {
  ArrowDown,
  ArrowLeft,
  ArrowUp,
  Bell,
  Check,
  CircleCheck,
  PanelRightOpen,
  Filter,
  Loader2,
  Mail,
  Pencil,
  Pin,
  PinOff,
  Plus,
  RefreshCw,
  Trash2,
  X,
} from "lucide-react";
import { toast } from "sonner";
import { ConversationDetails } from "@/components/conversation/ConversationDetails";
import { useUserAgents } from "@/hooks/agent/useAgents";
import {
  useDeleteInboxView,
  useDeleteInboxViewColumn,
  useInboxView,
  useRefineInboxView,
  useRenameInboxViewColumn,
  useRerunInboxView,
  useSetInboxViewRowHandled,
  useUpdateInboxView,
} from "@/hooks/inboxView/useInboxViews";
import { supabase } from "@/integrations/supabase/client";
import { describeScreenedOut } from "./components/criteriaText";
import { formatCell } from "./components/formatCell";
import { GenieAttribution } from "./components/GenieAttribution";
import { GenieScopePicker } from "./components/GenieScopePicker";
import { deriveRefineChips } from "./components/refineChips";
import { ThinkingPanel } from "./components/ThinkingPanel";
import { ViewCriteriaEditor } from "./components/ViewCriteriaEditor";
import { ViewDownloadMenu } from "./components/ViewDownloadMenu";
import { ViewInstantAlert } from "./components/ViewInstantAlert";
import { ViewReportSchedule } from "./components/ViewReportSchedule";

import type {
  InboxViewColumnType,
  InboxViewDetailProps,
  InboxViewRow,
  InboxViewRowScores,
  InboxViewRun,
} from "@/types/inboxView/inboxView";
import { GenieIcon } from "@/components/ui/genie-icons/GenieIcon";

type ScoreKey = keyof InboxViewRowScores;

const SCORE_COLUMNS: { key: ScoreKey; label: string; hint: string }[] = [
  { key: "match", label: "Match", hint: "How well the conversation fits this view's conditions" },
  { key: "urgency", label: "Urgency", hint: "How quickly it needs action" },
  { key: "intent", label: "Intent", hint: "How strong the intent to buy, book or order is" },
];

const scoreTone = (value: number) =>
  value >= 70 ? "text-genie-primary font-semibold" : value >= 40 ? "text-[#222B45]" : "text-[#94A3B8]";

const ScoreValue = ({ value }: { value: number | undefined }) =>
  typeof value === "number" ? (
    <span className={scoreTone(value)}>{value}</span>
  ) : (
    <span className="text-[#B6C0CC]">—</span>
  );

// Columns whose values are always short, so they can give their width to the rest.
const NARROW_COLUMN_TYPES = new Set<InboxViewColumnType>(["boolean", "date", "number"]);

const NEGATION_WORDS = new Set(["drop", "hide", "exclude", "without", "not", "remove", "no"]);
const STOPWORDS = new Set([
  "the",
  "a",
  "an",
  "of",
  "to",
  "and",
  "or",
  "but",
  "is",
  "are",
  "was",
  "were",
  "be",
  "been",
  "with",
  "for",
  "from",
  "in",
  "on",
  "at",
  "by",
  "as",
  "this",
  "that",
  "these",
  "those",
  "rows",
  "row",
  "ones",
  "one",
  "show",
  "only",
  "any",
  "all",
  "some",
  "leads",
  "lead",
  "have",
  "has",
  "had",
  "where",
  "when",
]);

// Client-side filter applied to rendered rows. Refine returns natural-language
// expressions like "drop the cold leads" or "only show hot ones"; we expand
// them into substring matches across cell values, honoring negation words.
const matchesClientFilter = (expression: string, haystack: string): boolean => {
  const words = expression
    .toLowerCase()
    .replace(/[^a-z0-9\s]/g, " ")
    .split(/\s+/)
    .filter(Boolean);
  const negated = words.some((w) => NEGATION_WORDS.has(w));
  const terms = words.filter((w) => !NEGATION_WORDS.has(w) && !STOPWORDS.has(w) && w.length > 1);
  if (terms.length === 0) return true; // nothing meaningful to match on — keep the row
  const anyHit = terms.some((t) => haystack.includes(t));
  return negated ? !anyHit : anyHit;
};

export const ViewDetail = ({ viewId, onBack }: InboxViewDetailProps) => {
  const [sourceConversationId, setSourceConversationId] = useState<string | null>(null);
  const { data, isLoading } = useInboxView(viewId);
  const { data: agents = [] } = useUserAgents(100);
  const refine = useRefineInboxView();
  const rerun = useRerunInboxView();
  const del = useDeleteInboxView();
  const update = useUpdateInboxView();
  const renameColumn = useRenameInboxViewColumn();
  const deleteColumn = useDeleteInboxViewColumn();
  const setRowHandled = useSetInboxViewRowHandled();
  const [scoreSort, setScoreSort] = useState<{ key: ScoreKey; descending: boolean } | null>(null);

  const [refineText, setRefineText] = useState("");
  const [clientFilters, setClientFilters] = useState<string[]>([]);
  // column name → filter string (substring match, case-insensitive)
  const [colFilters, setColFilters] = useState<Record<string, string>>({});
  const [openFilterCol, setOpenFilterCol] = useState<string | null>(null);
  const filterPopoverRef = useRef<HTMLDivElement>(null);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [editingName, setEditingName] = useState(false);
  const [draftName, setDraftName] = useState("");
  const [editingGenies, setEditingGenies] = useState(false);
  const [draftAgentFilter, setDraftAgentFilter] = useState<string[] | null>(null);
  // column being renamed (name being edited) + its draft text
  const [renamingCol, setRenamingCol] = useState<string | null>(null);
  const [renameDraft, setRenameDraft] = useState("");
  const [confirmDeleteCol, setConfirmDeleteCol] = useState<string | null>(null);
  const [editingColumns, setEditingColumns] = useState(false);
  const [addingColumn, setAddingColumn] = useState(false);
  const [addColumnText, setAddColumnText] = useState("");
  const [refineOpen, setRefineOpen] = useState(false);
  const [reportOpen, setReportOpen] = useState(false);
  const [alertOpen, setAlertOpen] = useState(false);
  // Track the run we just kicked off so we can toast its result once polling
  // sees it land. Cleared after the toast fires.
  const awaitingRunIdRef = useRef<string | null>(null);
  // Track the previous activeRun id so we can detect the active→complete edge.
  const prevActiveRunIdRef = useRef<string | null>(null);
  // The view whose one-off upgrade this page has already started, and the run doing it.
  const upgradeStartedForRef = useRef<string | null>(null);
  const [upgradeRunId, setUpgradeRunId] = useState<string | null>(null);
  // When an active run just completed, hold the ThinkingPanel on screen for a
  // beat so the celebration animation has time to play.
  const [celebrationRun, setCelebrationRun] = useState<InboxViewRun | null>(null);

  useEffect(() => {
    setSourceConversationId(null);
  }, [viewId]);

  // When the awaited run lands and polling sees it as the view's last_run,
  // surface the result to the customer and clear the ref.
  useEffect(() => {
    const lastRun = data?.view?.last_run;
    if (!awaitingRunIdRef.current) return;
    if (!data?.view || !lastRun) return;
    if (lastRun.id !== awaitingRunIdRef.current) return;
    if (data.activeRun) return; // still in flight

    awaitingRunIdRef.current = null;
    const added = lastRun.transcripts_extracted ?? 0;
    if (lastRun.status === "complete" && added === 0) {
      toast.success("Your view is up to date — no new conversations since last refresh.");
    } else if (lastRun.status === "complete") {
      toast.success(`Added ${added} new ${added === 1 ? "row" : "rows"}.`);
    } else if (lastRun.status === "partial") {
      toast.warning(`Added ${added} ${added === 1 ? "row" : "rows"}. Some conversations couldn't be processed.`);
    } else if (lastRun.status === "failed") {
      toast.error("Refresh failed. Please try again.");
    }
  }, [data]);

  // Refs mirroring the latest render's state, read from inside the realtime
  // subscription below so the channel itself only needs to (re)subscribe when
  // viewId changes, not on every poll tick or agents refetch.
  const latestRef = useRef({ data, agents, isRerunPending: rerun.isPending });
  latestRef.current = { data, agents, isRerunPending: rerun.isPending };

  // Live auto-refresh: while this view is open, silently trigger the same
  // refresh the manual button does when one of the view's own conversations
  // finishes processing. The "conversations" broadcast channel is global and
  // unscoped across every customer on the platform (used elsewhere purely for
  // harmless cache-invalidation), so every "completed" event is filtered
  // client-side against this view's scope before triggering a real, paid
  // extraction pass. A short debounce coalesces a burst of the customer's own
  // conversations completing in a tight window into a single refresh.
  useEffect(() => {
    let debounceTimer: number | null = null;
    let firing = false;

    const channel = supabase
      .channel("conversations")
      .on("broadcast", { event: "completed" }, (message: { payload?: { agent_id?: string | null } }) => {
        const agentId = message.payload?.agent_id;
        if (!agentId) return; // no scoping info — safer to skip than to over-fire

        const { data: latestData, agents: latestAgents, isRerunPending } = latestRef.current;
        const scopedView = latestData?.view;
        if (!scopedView) return;

        const inScope =
          scopedView.agent_filter && scopedView.agent_filter.length > 0
            ? scopedView.agent_filter.includes(agentId)
            : latestAgents.some((a) => a.id === agentId);
        if (!inScope) return;

        if (firing || isRerunPending || awaitingRunIdRef.current || latestData?.activeRun) return;
        firing = true;

        if (debounceTimer) window.clearTimeout(debounceTimer);
        debounceTimer = window.setTimeout(() => {
          firing = false;
          toast.info("New conversations detected — updating your view...");
          rerun.mutate(viewId, {
            onSuccess: (res) => {
              awaitingRunIdRef.current = res.run?.id ?? null;
            },
          });
        }, 4000);
      })
      .subscribe();

    return () => {
      if (debounceTimer) window.clearTimeout(debounceTimer);
      channel.unsubscribe();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewId]);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (filterPopoverRef.current && !filterPopoverRef.current.contains(e.target as Node)) {
        setOpenFilterCol(null);
      }
    };
    if (openFilterCol) document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [openFilterCol]);

  // Detect active-run → no-active-run transition and play a brief celebration.
  useEffect(() => {
    const currentActiveId = data?.activeRun?.id ?? null;
    const justCompleted = prevActiveRunIdRef.current && !currentActiveId && data?.view?.last_run;

    if (justCompleted && data?.view?.last_run) {
      setCelebrationRun(data.view.last_run as InboxViewRun);
      const t = window.setTimeout(() => setCelebrationRun(null), 2500);
      prevActiveRunIdRef.current = null;
      return () => window.clearTimeout(t);
    }

    if (currentActiveId) {
      prevActiveRunIdRef.current = currentActiveId;
    }
  }, [data?.activeRun?.id, data?.view?.last_run]);

  // A view made before screening rules existed is upgraded the first time it
  // is opened: rules are worked out from its description and every row is
  // checked against them. The banner below tells the customer while it runs.
  useEffect(() => {
    const openedView = data?.view;
    if (!openedView || openedView.screened_at || data.activeRun) return;
    if (upgradeStartedForRef.current === viewId) return;
    upgradeStartedForRef.current = viewId;
    rerun.mutate(viewId, {
      onSuccess: (res) => setUpgradeRunId(res.run?.id ?? null),
      onError: () => {
        upgradeStartedForRef.current = null;
      },
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewId, data?.view?.screened_at, data?.activeRun?.id]);

  // Report the outcome once the upgrade run has landed.
  useEffect(() => {
    const lastRun = data?.view?.last_run;
    if (!upgradeRunId || data?.activeRun || lastRun?.id !== upgradeRunId) return;
    setUpgradeRunId(null);
    if (lastRun.status === "failed") {
      toast.error("We couldn't finish upgrading this view. Press Refresh to try again.");
    } else {
      toast.success(`This view now uses smarter matching. ${lastRun.status_message ?? ""}`.trim());
    }
  }, [data, upgradeRunId]);

  if (isLoading || !data) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-6 w-6 animate-spin text-genie-primary" />
      </div>
    );
  }

  const view = data.view;
  if (!view) return <div className="py-8 text-center text-[#64748B]">View not found.</div>;

  const cols = view.column_schema ?? [];
  const allRows = data.rows ?? [];
  const scopedAgents = view.agent_filter && view.agent_filter.length > 0
    ? agents.filter((a) => view.agent_filter!.includes(a.id))
    : agents;
  const agentSuggestedEmails = [...new Set<string>(scopedAgents.flatMap((a) => a.report_recipients ?? []))];
  const activeRun = data.activeRun;
  const isActive = !!activeRun;
  const lastRun = view.last_run;
  // Apply any plain-English filters the customer has stacked via Refine. These
  // are interpreted client-side as substring matches across cell values — good
  // enough for "drop the cold leads"-style filters without a second AI pass.
  const activeColFilters = Object.entries(colFilters).filter(([, v]) => v.trim() !== "");
  const rows = allRows.filter((row) => {
    // Plain-English refine filters (cross-column)
    if (clientFilters.length > 0) {
      const haystack = Object.values(row.data ?? {})
        .map((v) => (v == null ? "" : String(v).toLowerCase()))
        .join(" ");
      if (!clientFilters.every((expr) => matchesClientFilter(expr, haystack))) return false;
    }
    // Per-column substring filters
    if (activeColFilters.length > 0) {
      for (const [col, term] of activeColFilters) {
        const colSchema = cols.find((c) => c.name === col);
        const raw = row.data?.[col];
        let cellVal: string;
        if (colSchema?.type === "boolean") {
          // Include yes/no aliases alongside true/false so both work as filter terms
          if (raw === true || raw === "true") cellVal = "true yes";
          else if (raw === false || raw === "false") cellVal = "false no";
          else cellVal = String(raw ?? "").toLowerCase();
        } else {
          cellVal = String(raw ?? "").toLowerCase();
        }
        if (!cellVal.includes(term.toLowerCase().trim())) return false;
      }
    }
    return true;
  });
  const hasScores = allRows.some((row) => !!row.scores);
  const sortedRows = scoreSort
    ? [...rows].sort((a, b) => {
        const diff = (a.scores?.[scoreSort.key] ?? -1) - (b.scores?.[scoreSort.key] ?? -1);
        return scoreSort.descending ? -diff : diff;
      })
    : rows;
  const cycleScoreSort = (key: ScoreKey) =>
    setScoreSort((prev) =>
      prev?.key !== key ? { key, descending: true } : prev.descending ? { key, descending: false } : null,
    );
  const screenedOut = data.screenedOut;
  const tableColSpan = cols.length + 1 + (hasScores ? SCORE_COLUMNS.length : 0) + (editingColumns ? 1 : 0);
  const refineChips = deriveRefineChips(cols, rows);
  const displayName = view.name?.trim() || view.prompt.split("\n")[0];

  const submitRefine = () => {
    const text = refineText.trim();
    if (!text) return;
    refine.mutate(
      { viewId, refinement: text },
      {
        onSuccess: (result) => {
          if (result.kind === "filter" && result.clientFilter) {
            setClientFilters((prev) => [...prev, result.clientFilter!]);
          }
          setRefineText("");
        },
      },
    );
  };

  const submitAddColumn = () => {
    const text = addColumnText.trim();
    if (!text) return;
    const refinement = /add|include/i.test(text) ? text : `Add a column for ${text}`;
    refine.mutate(
      { viewId, refinement },
      {
        onSuccess: () => {
          setAddColumnText("");
          setAddingColumn(false);
        },
      },
    );
  };

  const startRename = () => {
    setDraftName(view.name ?? "");
    setEditingName(true);
  };

  const commitRename = () => {
    const next = draftName.trim();
    if (next === (view.name ?? "").trim()) {
      setEditingName(false);
      return;
    }
    update.mutate({ viewId, name: next.length > 0 ? next : null }, { onSuccess: () => setEditingName(false) });
  };

  const togglePin = () => {
    update.mutate({ viewId, pinned: !view.pinned });
  };

  const startEditingGenies = () => {
    setDraftAgentFilter(view.agent_filter);
    setEditingGenies(true);
  };

  const commitAgentFilter = () => {
    setEditingGenies(false);
    const current = view.agent_filter ?? null;
    const next = draftAgentFilter ?? null;
    const unchanged =
      (current === null && next === null) ||
      (Array.isArray(current) &&
        Array.isArray(next) &&
        current.length === next.length &&
        current.every((id) => next.includes(id)));
    if (unchanged) return;
    update.mutate({ viewId, agent_filter: next });
  };

  const includeRecordings = view.include_recordings === true;

  const toggleRecordings = () => {
    update.mutate({ viewId, include_recordings: !includeRecordings });
  };

  const startRenameColumn = (colName: string) => {
    setRenamingCol(colName);
    setRenameDraft(colName);
  };

  const commitRenameColumn = () => {
    if (!renamingCol) return;
    const to = renameDraft.trim();
    if (!to || to === renamingCol) {
      setRenamingCol(null);
      return;
    }
    const from = renamingCol;
    setRenamingCol(null);
    renameColumn.mutate(
      { viewId, from, to, regenerate: false },
      {
        onSuccess: () => {
          if (
            window.confirm(`Renamed "${from}" to "${to}". Regenerate this column's data now to match the new name?`)
          ) {
            renameColumn.mutate({ viewId, from: to, to, regenerate: true });
          }
        },
      },
    );
  };

  const handleDeleteColumn = (colName: string) => {
    setConfirmDeleteCol(null);
    deleteColumn.mutate({ viewId, name: colName });
  };

  const openSourceConversation = (rowConversationId: number | null) => {
    if (!rowConversationId) return;
    setSourceConversationId(String(rowConversationId));
  };

  // Two empty-state cases the customer should be able to tell apart:
  //   (a) no conversations in the view's genies and date range at all
  //   (b) conversations were checked but none passed the view's conditions
  const checkedCount = Math.max(lastRun?.transcripts_attempted ?? 0, screenedOut?.total ?? 0);
  const emptyState =
    checkedCount > 0 ? (
      <>
        <div className="font-medium text-[#222B45] mb-1">
          Checked {checkedCount} {checkedCount === 1 ? "conversation" : "conversations"} — none met this view&rsquo;s
          conditions.
        </div>
        <div>
          {screenedOut && screenedOut.total > 0 ? `${describeScreenedOut(screenedOut)}. ` : ""}
          Open Refine to see the conditions, loosen one, or lower the minimum match.
        </div>
      </>
    ) : (
      <>
        <div className="font-medium text-[#222B45] mb-1">No conversations matched.</div>
        <div>
          Try Refine at the top: e.g. &ldquo;broaden the date range&rdquo; or &ldquo;include all genies&rdquo;.
        </div>
      </>
    );

  // Mark-handled and open-conversation buttons, shared by the table and the card list.
  const renderRowActions = (row: InboxViewRow) => {
    const handled = !!row.handled_at;
    return (
      <>
        {row.id && (
          <button
            type="button"
            aria-label={handled ? "Mark as not handled" : "Mark as handled"}
            title={handled ? "Handled — click to reopen" : "Mark as handled"}
            aria-pressed={handled}
            disabled={setRowHandled.isPending}
            className={`inline-flex min-h-[44px] min-w-[44px] items-center justify-center rounded-lg transition-colors disabled:opacity-50 ${
              handled
                ? "text-genie-primary bg-genie-primary/10"
                : "text-[#B6C0CC] hover:text-genie-primary hover:bg-genie-primary/10"
            }`}
            onClick={(event) => {
              event.stopPropagation();
              setRowHandled.mutate({ viewId, rowId: row.id!, handled: !handled });
            }}
          >
            <CircleCheck className="h-4 w-4" />
          </button>
        )}
        {!!row.source_conversation_id && (
          <button
            type="button"
            aria-label="Open source conversation"
            className="inline-flex min-h-[44px] min-w-[44px] items-center justify-center rounded-lg text-genie-primary hover:bg-genie-primary/10"
            onClick={(event) => {
              event.stopPropagation();
              openSourceConversation(row.source_conversation_id);
            }}
          >
            <PanelRightOpen className="h-4 w-4" />
          </button>
        )}
      </>
    );
  };

  return (
    <div>
      {/* Header */}
      <div className="flex flex-wrap items-start gap-3 mb-5">
        <button
          onClick={onBack}
          className="mt-1 p-2 rounded-lg hover:bg-white border border-transparent hover:border-[#E5EAF0] transition-colors shrink-0"
          aria-label="Back to views"
        >
          <ArrowLeft className="h-4 w-4 text-[#64748B]" />
        </button>
        <div className="flex-1 min-w-0">
          {editingName ? (
            <div className="flex items-center gap-2">
              <input
                autoFocus
                type="text"
                value={draftName}
                onChange={(e) => setDraftName(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") commitRename();
                  if (e.key === "Escape") setEditingName(false);
                }}
                placeholder="Name this view"
                className="flex-1 text-xl font-bold text-[#222B45] bg-white border border-[#E5EAF0] rounded-lg px-3 py-1.5 outline-none focus:border-genie-primary focus:ring-4 focus:ring-genie-primary/10"
                maxLength={80}
              />
              <button
                onClick={commitRename}
                disabled={update.isPending}
                className="p-2 rounded-lg bg-genie-primary/10 text-genie-primary hover:bg-genie-primary/15 transition-colors disabled:opacity-50"
                aria-label="Save name"
              >
                {update.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}
              </button>
              <button
                onClick={() => setEditingName(false)}
                className="p-2 rounded-lg text-[#64748B] hover:bg-white border border-transparent hover:border-[#E5EAF0] transition-colors"
                aria-label="Cancel rename"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          ) : (
            <div className="flex items-center gap-2 group">
              <h2 className="text-xl font-bold text-[#222B45]">{displayName}</h2>
              <button
                onClick={startRename}
                className="p-1 rounded text-[#B6C0CC] hover:text-genie-primary opacity-0 group-hover:opacity-100 transition-opacity"
                aria-label="Rename view"
                title="Rename view"
              >
                <Pencil className="h-3.5 w-3.5" />
              </button>
            </div>
          )}
          <div className="mt-2">
            {editingGenies ? (
              <div className="flex items-center gap-2">
                <GenieScopePicker
                  selected={draftAgentFilter}
                  onChange={setDraftAgentFilter}
                  disabled={update.isPending}
                />
                <button
                  onClick={commitAgentFilter}
                  disabled={update.isPending}
                  className="p-1.5 rounded-lg bg-genie-primary/10 text-genie-primary hover:bg-genie-primary/15 transition-colors disabled:opacity-50"
                  aria-label="Save Genie selection"
                  title="Save"
                >
                  {update.isPending ? (
                    <Loader2 className="h-3.5 w-3.5 animate-spin" />
                  ) : (
                    <Check className="h-3.5 w-3.5" />
                  )}
                </button>
                <button
                  onClick={() => setEditingGenies(false)}
                  className="p-1.5 rounded-lg text-[#64748B] hover:bg-white border border-transparent hover:border-[#E5EAF0] transition-colors"
                  aria-label="Cancel"
                  title="Cancel"
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              </div>
            ) : (
              <div className="flex flex-wrap items-center gap-2">
                <div className="inline-flex items-center gap-2 group">
                  <GenieAttribution agentIds={view.agent_filter} agents={agents} label="Analyzing" size={22} />
                  <button
                    onClick={startEditingGenies}
                    className="p-1 rounded text-[#B6C0CC] hover:text-genie-primary opacity-0 group-hover:opacity-100 transition-opacity"
                    aria-label="Edit Genies"
                    title="Edit Genies"
                  >
                    <Pencil className="h-3.5 w-3.5" />
                  </button>
                </div>
                <button
                  type="button"
                  onClick={toggleRecordings}
                  disabled={update.isPending}
                  aria-pressed={includeRecordings}
                  className={`shrink-0 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-semibold border transition-colors disabled:opacity-50 ${
                    includeRecordings
                      ? "border-genie-primary/40 bg-genie-primary/10 text-genie-primary"
                      : "border-[#E5EAF0] bg-white text-[#64748B] hover:border-genie-primary/40 hover:text-genie-primary"
                  }`}
                  title={
                    includeRecordings
                      ? "Meeting recordings and genie meetings are screened by this view. Click to leave them out."
                      : "This view looks at genie calls and chats. Click to also screen meeting recordings and genie meetings."
                  }
                >
                  <span
                    className={`h-1.5 w-1.5 rounded-full ${includeRecordings ? "bg-genie-primary" : "bg-[#B6C0CC]"}`}
                    aria-hidden
                  />
                  {includeRecordings ? "Meetings included" : "Meetings left out"}
                </button>
              </div>
            )}
          </div>
          <div className="mt-2 flex items-center gap-2 min-w-0">
            <p className="text-xs text-[#64748B] truncate min-w-0" title={view.prompt}>
              From your prompt: {view.prompt.replace(/\n/g, " · ")}
            </p>
            <button
              type="button"
              onClick={() => setRefineOpen((v) => !v)}
              aria-expanded={refineOpen}
              aria-controls="view-refine-panel"
              className={`shrink-0 inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-semibold border transition-colors ${
                refineOpen
                  ? "border-genie-primary/40 bg-genie-primary/10 text-genie-primary"
                  : "border-[#E5EAF0] bg-white text-[#64748B] hover:border-genie-primary/40 hover:text-genie-primary"
              }`}
              title="Change this view in plain English"
            >
              <GenieIcon name="sparkles" size={12} color="#4E9CFF" label="" className="shrink-0" />
              Refine
            </button>
          </div>
          <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-[#64748B]">
            <span>
              <strong className="text-[#222B45]">{rows.length}</strong> {rows.length === 1 ? "row" : "rows"}
              {(clientFilters.length > 0 || activeColFilters.length > 0) && allRows.length !== rows.length && (
                <span className="ml-1 text-[#B6C0CC]">of {allRows.length}</span>
              )}
            </span>
            <span>·</span>
            <span>{cols.length} columns</span>
            {lastRun?.completed_at && !isActive && (
              <>
                <span>·</span>
                <span>refreshed {new Date(lastRun.completed_at).toLocaleString()}</span>
              </>
            )}
            {screenedOut && screenedOut.total > 0 && (
              <>
                <span>·</span>
                <span title={describeScreenedOut(screenedOut)}>
                  {screenedOut.total} checked and left out
                  <span className="sm:hidden"> ({describeScreenedOut(screenedOut)})</span>
                </span>
              </>
            )}
          </div>
          {(clientFilters.length > 0 || activeColFilters.length > 0) && (
            <div className="mt-2 flex flex-wrap gap-1.5">
              <span className="text-[10px] uppercase tracking-wider font-semibold text-[#64748B] self-center">
                Active filters:
              </span>
              {clientFilters.map((f, i) => (
                <span
                  key={`refine-${i}`}
                  className="inline-flex items-center gap-1 text-[11px] font-medium px-2 py-0.5 rounded-full bg-genie-primary/10 text-genie-primary"
                >
                  {f}
                  <button
                    onClick={() => setClientFilters((prev) => prev.filter((_, idx) => idx !== i))}
                    className="hover:opacity-70"
                    aria-label="Remove filter"
                  >
                    ×
                  </button>
                </span>
              ))}
              {activeColFilters.map(([col, term]) => (
                <span
                  key={`col-${col}`}
                  className="inline-flex items-center gap-1 text-[11px] font-medium px-2 py-0.5 rounded-full bg-genie-primary/10 text-genie-primary"
                >
                  <span className="opacity-60">{col}:</span> {term}
                  <button
                    onClick={() =>
                      setColFilters((prev) => {
                        const next = { ...prev };
                        delete next[col];
                        return next;
                      })
                    }
                    className="hover:opacity-70"
                    aria-label="Remove filter"
                  >
                    ×
                  </button>
                </span>
              ))}
            </div>
          )}
        </div>
        <div className="flex items-center gap-2 shrink-0 w-full sm:w-auto justify-end sm:justify-start order-last sm:order-none">
          <button
            type="button"
            onClick={togglePin}
            disabled={update.isPending}
            className={`p-2 rounded-xl border transition-colors disabled:opacity-50 ${
              view.pinned
                ? "border-genie-primary/40 bg-genie-primary/10 text-genie-primary"
                : "border-[#E5EAF0] bg-white text-[#64748B] hover:border-genie-primary/40 hover:text-genie-primary"
            }`}
            title={view.pinned ? "Unpin view" : "Pin view"}
            aria-label={view.pinned ? "Unpin view" : "Pin view"}
          >
            {view.pinned ? <Pin className="h-4 w-4 fill-current" /> : <PinOff className="h-4 w-4" />}
          </button>
          <button
            type="button"
            onClick={() => setReportOpen(true)}
            className={`inline-flex items-center gap-1.5 px-2.5 sm:px-3 py-2 rounded-xl text-sm font-medium border transition-colors ${
              view.report_frequency
                ? "border-genie-primary/40 bg-genie-primary/10 text-genie-primary"
                : "border-[#E5EAF0] bg-white text-[#222B45] hover:border-genie-primary/40 hover:text-genie-primary"
            }`}
            title={view.report_frequency ? "Scheduled email report is on" : "Set up a scheduled email report"}
            aria-label="Scheduled email report"
          >
            <Mail className="h-4 w-4" />
            <span className="hidden sm:inline">Report</span>
          </button>
          <button
            type="button"
            onClick={() => setAlertOpen(true)}
            className={`inline-flex items-center gap-1.5 px-2.5 sm:px-3 py-2 rounded-xl text-sm font-medium border transition-colors ${
              view.instant_alert?.enabled
                ? "border-genie-primary/40 bg-genie-primary/10 text-genie-primary"
                : "border-[#E5EAF0] bg-white text-[#222B45] hover:border-genie-primary/40 hover:text-genie-primary"
            }`}
            title={view.instant_alert?.enabled ? "Instant alert is on" : "Set up an instant alert"}
            aria-label="Instant alert"
          >
            <Bell className="h-4 w-4" />
            <span className="hidden sm:inline">Alert</span>
          </button>
          <ViewDownloadMenu view={view} viewName={displayName} columns={cols} rows={sortedRows} />
          <button
            onClick={() =>
              rerun.mutate(viewId, {
                onSuccess: (res) => {
                  awaitingRunIdRef.current = res.run?.id ?? null;
                  if (res.alreadyRunning) {
                    toast.info("A refresh is already running for this view.");
                  }
                },
              })
            }
            disabled={isActive || rerun.isPending}
            className="inline-flex items-center gap-1.5 px-2.5 sm:px-3 py-2 rounded-xl text-sm font-medium border border-[#E5EAF0] bg-white text-[#222B45] hover:border-genie-primary/40 hover:text-genie-primary transition-colors disabled:opacity-50"
            title="Bring in new conversations since your last refresh. Your existing rows, notes and edits stay put."
          >
            <RefreshCw className={`h-4 w-4 ${rerun.isPending ? "animate-spin" : ""}`} />
            <span className="hidden sm:inline">Refresh</span>
          </button>
          {confirmDelete ? (
            <div className="inline-flex items-center gap-1 px-2 py-1 rounded-xl border border-[#E5484D]/40 bg-[#FFE9E9]">
              <span className="text-xs font-semibold text-[#E5484D] px-1">Delete?</span>
              <button
                onClick={() => del.mutate(viewId, { onSuccess: onBack })}
                disabled={del.isPending}
                className="px-2 py-1 rounded-lg text-xs font-semibold text-white bg-[#E5484D] hover:opacity-90 disabled:opacity-50"
              >
                {del.isPending ? <Loader2 className="h-3 w-3 animate-spin" /> : "Yes"}
              </button>
              <button
                onClick={() => setConfirmDelete(false)}
                className="px-2 py-1 rounded-lg text-xs font-semibold text-[#64748B] hover:text-[#222B45]"
              >
                Cancel
              </button>
            </div>
          ) : (
            <button
              onClick={() => setConfirmDelete(true)}
              disabled={del.isPending}
              className="p-2 rounded-xl text-[#64748B] hover:text-[#E5484D] hover:bg-white border border-transparent hover:border-[#E5EAF0] transition-colors disabled:opacity-50"
              aria-label="Delete view"
              title="Delete view"
            >
              <Trash2 className="h-4 w-4" />
            </button>
          )}
        </div>
      </div>

      {/* Refine */}
      {refineOpen && (
        <div id="view-refine-panel" className="mb-5 bg-white border border-[#E5EAF0] rounded-2xl p-4">
          <div className="text-[10px] uppercase tracking-wider font-semibold text-[#64748B] mb-2">
            Refine in plain English
          </div>
          <div className="flex items-center gap-2 border border-[#E5EAF0] rounded-xl px-3 py-2 focus-within:border-genie-primary focus-within:ring-4 focus-within:ring-genie-primary/10 transition-all">
            <GenieIcon name="sparkles" size={16} color="#4E9CFF" label="Refine" className="shrink-0" />
            <input
              autoFocus
              type="text"
              value={refineText}
              onChange={(e) => setRefineText(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") submitRefine();
              }}
              placeholder="e.g. drop the cold leads, add a phone column, group by pickup city..."
              disabled={refine.isPending}
              className="flex-1 outline-none border-0 text-sm bg-transparent text-[#222B45] placeholder:text-[#B6C0CC] disabled:opacity-60"
            />
            <button
              onClick={submitRefine}
              disabled={refine.isPending || !refineText.trim()}
              className="shrink-0 px-3 py-1.5 rounded-lg text-white text-xs font-semibold disabled:opacity-50"
              style={{ background: "linear-gradient(90deg, #4E9CFF 0%, #FF4DB8 100%)" }}
            >
              {refine.isPending ? <Loader2 className="h-3 w-3 animate-spin" /> : "Send"}
            </button>
          </div>
          <div className="mt-2 flex flex-wrap gap-1.5">
            {refineChips.map((chip) => (
              <button
                key={chip}
                onClick={() => setRefineText(chip)}
                disabled={refine.isPending}
                className="text-[11px] px-2.5 py-1 rounded-full border border-[#E5EAF0] bg-white text-[#64748B] hover:border-genie-primary/40 hover:text-genie-primary transition-colors disabled:opacity-60"
              >
                {chip}
              </button>
            ))}
          </div>
          {refine.data?.message && refine.isSuccess && (
            <div className="mt-2 text-xs text-[#64748B]">{refine.data.message}</div>
          )}
          <ViewCriteriaEditor
            view={view}
            isSaving={update.isPending}
            onSave={(patch) =>
              update.mutate(
                { viewId, ...patch },
                {
                  onSuccess: (result) =>
                    result.run
                      ? toast.info("Saved. Checking conversations against the new conditions...")
                      : toast.success("Saved. The table is up to date."),
                  onError: (error) => toast.error(error instanceof Error ? error.message : "Could not save the changes."),
                },
              )
            }
          />
        </div>
      )}

      {(upgradeRunId || !view.screened_at) && (
        <div className="mb-5 rounded-2xl p-[2px] bg-gradient-to-r from-[#4E9CFF] to-[#FF4DB8]" role="status">
          <div className="rounded-[14px] bg-white p-4">
            <div className="flex items-center gap-2 text-sm font-semibold text-[#222B45]">
              <GenieIcon name="sparkles" size={16} color="#4E9CFF" label="" className="shrink-0" />
              We&rsquo;re upgrading this view to smarter matching
            </div>
            <p className="mt-1 text-xs text-[#64748B]">
              Your Genies now check every conversation against what you asked for, instead of adding any conversation
              that mentions the topic. Rows that don&rsquo;t meet the view&rsquo;s conditions are removed; the rest
              stay as they are. This happens once and takes a minute or two. Afterwards, open Refine to see or change
              the conditions.
            </p>
          </div>
        </div>
      )}

      {/* Active run progress + celebration moment */}
      {isActive && activeRun ? (
        <div className="mb-5">
          <ThinkingPanel
            status={activeRun.status as "designing" | "extracting" | "pending"}
            message={activeRun.status_message}
            transcriptsAttempted={activeRun.transcripts_attempted}
            transcriptsExtracted={activeRun.transcripts_extracted}
            columnNames={cols.map((c) => c.name)}
          />
        </div>
      ) : celebrationRun ? (
        <div className="mb-5">
          <ThinkingPanel
            status="complete"
            transcriptsExtracted={celebrationRun.transcripts_extracted}
            transcriptsAttempted={celebrationRun.transcripts_attempted}
          />
        </div>
      ) : null}

      {/* Table */}
      <div className="bg-white border border-[#E5EAF0] rounded-2xl overflow-hidden">
        {cols.length === 0 ? (
          <div className="py-12 text-center text-sm text-[#64748B]">
            No columns yet. Refresh or refine the view to get started.
          </div>
        ) : (
          <>
            {/* Fixed layout makes the columns share the available width and wrap
                their text, so every column stays on screen without sideways
                scrolling. Short-valued columns get a narrow fixed width. */}
            <div className="hidden md:block overflow-x-auto">
            <table className="w-full table-fixed text-xs xl:text-sm">
              <thead>
                <tr className="bg-[#FAFCFF] border-b border-[#E5EAF0]">
                  {cols.map((c) => {
                    const hasFilter = !!colFilters[c.name]?.trim();
                    const isOpen = openFilterCol === c.name;
                    return (
                      <th
                        key={c.name}
                        className={`text-left px-2 xl:px-3 py-3 text-[10px] xl:text-[11px] font-semibold uppercase tracking-wide xl:tracking-wider text-[#64748B] [overflow-wrap:anywhere] ${
                          NARROW_COLUMN_TYPES.has(c.type) ? "w-[84px] xl:w-24" : ""
                        }`}
                        title={c.description}
                      >
                        <div className="relative inline-flex items-center gap-1 group/col">
                          {renamingCol === c.name ? (
                            <input
                              autoFocus
                              type="text"
                              value={renameDraft}
                              onChange={(e) => setRenameDraft(e.target.value)}
                              onKeyDown={(e) => {
                                if (e.key === "Enter") commitRenameColumn();
                                if (e.key === "Escape") setRenamingCol(null);
                              }}
                              onBlur={commitRenameColumn}
                              onClick={(e) => e.stopPropagation()}
                              className="text-[11px] font-semibold uppercase tracking-wider text-[#222B45] bg-white border border-genie-primary rounded px-1.5 py-0.5 outline-none w-28"
                            />
                          ) : (
                            <span>{c.name}</span>
                          )}
                          {editingColumns && (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                startRenameColumn(c.name);
                              }}
                              className="p-0.5 rounded text-[#B6C0CC] hover:text-genie-primary transition-colors"
                              aria-label={`Rename ${c.name}`}
                              title={`Rename ${c.name}`}
                            >
                              <Pencil className="h-3 w-3" />
                            </button>
                          )}
                          {!editingColumns && (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                setOpenFilterCol(isOpen ? null : c.name);
                              }}
                              className={`p-0.5 rounded transition-colors ${hasFilter ? "text-genie-primary opacity-100" : "text-[#B6C0CC] opacity-0 group-hover/col:opacity-100"}`}
                              aria-label={`Filter by ${c.name}`}
                              title={`Filter by ${c.name}`}
                            >
                              <Filter className="h-3 w-3" />
                            </button>
                          )}
                          {editingColumns &&
                            (confirmDeleteCol === c.name ? (
                              <span className="inline-flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
                                <button
                                  onClick={() => handleDeleteColumn(c.name)}
                                  className="text-[10px] font-semibold text-[#E5484D] hover:opacity-80"
                                >
                                  Delete?
                                </button>
                                <button
                                  onClick={() => setConfirmDeleteCol(null)}
                                  className="text-[10px] font-semibold text-[#64748B] hover:text-[#222B45]"
                                >
                                  ×
                                </button>
                              </span>
                            ) : (
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setConfirmDeleteCol(c.name);
                                }}
                                className="p-0.5 rounded text-[#B6C0CC] hover:text-[#E5484D] transition-colors"
                                aria-label={`Delete ${c.name}`}
                                title={`Delete ${c.name}`}
                              >
                                <X className="h-3 w-3" />
                              </button>
                            ))}
                          {isOpen && (
                            <div
                              ref={filterPopoverRef}
                              className="absolute top-full left-0 mt-1 z-20 w-52 bg-white border border-[#E5EAF0] rounded-xl shadow-lg p-2"
                              onClick={(e) => e.stopPropagation()}
                            >
                              <div className="flex items-center gap-1.5 border border-[#E5EAF0] rounded-lg px-2 py-1.5 focus-within:border-genie-primary focus-within:ring-2 focus-within:ring-genie-primary/10 transition-all">
                                <input
                                  autoFocus
                                  type="text"
                                  value={colFilters[c.name] ?? ""}
                                  onChange={(e) => setColFilters((prev) => ({ ...prev, [c.name]: e.target.value }))}
                                  onKeyDown={(e) => {
                                    if (e.key === "Enter" || e.key === "Escape") setOpenFilterCol(null);
                                  }}
                                  placeholder={`Filter ${c.name}…`}
                                  className="flex-1 text-[12px] text-[#222B45] bg-transparent outline-none placeholder:text-[#B6C0CC] normal-case tracking-normal font-normal"
                                />
                                {colFilters[c.name] && (
                                  <button
                                    onClick={() => {
                                      setColFilters((prev) => {
                                        const next = { ...prev };
                                        delete next[c.name];
                                        return next;
                                      });
                                      setOpenFilterCol(null);
                                    }}
                                    className="text-[#B6C0CC] hover:text-[#64748B]"
                                    aria-label="Clear filter"
                                  >
                                    <X className="h-3 w-3" />
                                  </button>
                                )}
                              </div>
                            </div>
                          )}
                        </div>
                      </th>
                    );
                  })}
                  {hasScores &&
                    SCORE_COLUMNS.map((s) => {
                      const sorted = scoreSort?.key === s.key;
                      return (
                        <th key={s.key} className="w-16 xl:w-[72px] px-1.5 xl:px-2 py-3 text-right" title={s.hint}>
                          <button
                            type="button"
                            onClick={() => cycleScoreSort(s.key)}
                            aria-label={`Sort by ${s.label}`}
                            className={`inline-flex items-center gap-0.5 text-[10px] font-semibold uppercase tracking-wide transition-colors ${
                              sorted ? "text-genie-primary" : "text-[#64748B] hover:text-genie-primary"
                            }`}
                          >
                            {s.label}
                            {sorted &&
                              (scoreSort.descending ? <ArrowDown className="h-3 w-3" /> : <ArrowUp className="h-3 w-3" />)}
                          </button>
                        </th>
                      );
                    })}
                  <th className="w-24 px-2 py-2 text-right">
                    <button
                      onClick={() => {
                        setEditingColumns((v) => !v);
                        setConfirmDeleteCol(null);
                        setRenamingCol(null);
                        setOpenFilterCol(null);
                      }}
                      className={`p-1.5 rounded-lg transition-colors ${
                        editingColumns
                          ? "bg-genie-primary/10 text-genie-primary"
                          : "text-[#B6C0CC] hover:text-genie-primary hover:bg-white"
                      }`}
                      aria-label={editingColumns ? "Done editing columns" : "Edit columns"}
                      title={editingColumns ? "Done editing columns" : "Edit columns"}
                    >
                      <Pencil className="h-3.5 w-3.5" />
                    </button>
                  </th>
                  {editingColumns && (
                    <th className="w-48 px-3 py-2 text-left">
                      {addingColumn ? (
                        <div
                          className="flex items-center gap-1.5 border border-genie-primary rounded-lg px-2 py-1 bg-white"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <input
                            autoFocus
                            type="text"
                            value={addColumnText}
                            onChange={(e) => setAddColumnText(e.target.value)}
                            onKeyDown={(e) => {
                              if (e.key === "Enter") submitAddColumn();
                              if (e.key === "Escape") {
                                setAddingColumn(false);
                                setAddColumnText("");
                              }
                            }}
                            placeholder="e.g. phone number"
                            disabled={refine.isPending}
                            className="text-[12px] font-normal normal-case text-[#222B45] bg-transparent outline-none placeholder:text-[#B6C0CC] w-32"
                          />
                          <button
                            onClick={submitAddColumn}
                            disabled={refine.isPending || !addColumnText.trim()}
                            className="text-[#64748B] hover:text-genie-primary disabled:opacity-50"
                            aria-label="Add column"
                          >
                            {refine.isPending ? (
                              <Loader2 className="h-3 w-3 animate-spin" />
                            ) : (
                              <Check className="h-3 w-3" />
                            )}
                          </button>
                          <button
                            onClick={() => {
                              setAddingColumn(false);
                              setAddColumnText("");
                            }}
                            className="text-[#B6C0CC] hover:text-[#64748B]"
                            aria-label="Cancel"
                          >
                            <X className="h-3 w-3" />
                          </button>
                        </div>
                      ) : (
                        <button
                          onClick={() => setAddingColumn(true)}
                          className="inline-flex items-center gap-1 text-[11px] font-semibold normal-case text-genie-primary hover:opacity-80"
                        >
                          <Plus className="h-3 w-3" />
                          Add column
                        </button>
                      )}
                    </th>
                  )}
                </tr>
              </thead>
              <tbody>
                {rows.length === 0 && !isActive ? (
                  <tr>
                    <td colSpan={tableColSpan} className="py-12 text-center text-sm text-[#64748B]">
                      {emptyState}
                    </td>
                  </tr>
                ) : (
                  sortedRows.map((row) => {
                    const hasSource = !!row.source_conversation_id;
                    return (
                      <tr
                        key={row.id ?? row.row_idx}
                        onClick={() => openSourceConversation(row.source_conversation_id)}
                        className={`border-b border-[#E5EAF0] last:border-b-0 hover:bg-[#FAFCFF] ${hasSource ? "cursor-pointer" : ""} ${row.handled_at ? "opacity-60" : ""}`}
                        title={hasSource ? "Open the source conversation" : undefined}
                      >
                        {cols.map((c, j) => (
                          <td key={c.name} className="px-2 xl:px-3 py-3 align-top text-[#222B45] [overflow-wrap:anywhere]">
                            <span className={j === 0 ? "font-semibold" : ""}>
                              {formatCell(row.data?.[c.name], c.type)}
                            </span>
                            {j === 0 && row.match_reason && (
                              <div
                                className="mt-1 text-[10px] xl:text-[11px] font-normal leading-snug text-[#94A3B8] line-clamp-2"
                                title={row.match_reason}
                              >
                                {row.match_reason}
                              </div>
                            )}
                          </td>
                        ))}
                        {hasScores &&
                          SCORE_COLUMNS.map((s) => (
                            <td key={s.key} className="px-1.5 xl:px-2 py-3 align-top text-right tabular-nums">
                              <ScoreValue value={row.scores?.[s.key]} />
                            </td>
                          ))}
                        <td className="px-1 py-3 align-top text-right whitespace-nowrap">{renderRowActions(row)}</td>
                        {editingColumns && <td />}
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
            </div>

            {/* The table shrinks its text below xl; below md even that cannot
                fit a table this wide, so each row becomes a card
                that lists every column, with nothing to scroll sideways. */}
            <div className="md:hidden">
              {rows.length === 0 && !isActive ? (
                <div className="px-4 py-12 text-center text-sm text-[#64748B]">{emptyState}</div>
              ) : (
                <ul className="divide-y divide-[#E5EAF0]">
                  {sortedRows.map((row) => {
                    const hasSource = !!row.source_conversation_id;
                    const [first, ...rest] = cols;
                    return (
                      <li
                        key={row.id ?? row.row_idx}
                        onClick={() => openSourceConversation(row.source_conversation_id)}
                        className={`p-4 ${hasSource ? "cursor-pointer" : ""} ${row.handled_at ? "opacity-60" : ""}`}
                      >
                        <div className="flex items-start gap-2">
                          <div className="min-w-0 flex-1">
                            <div className="text-[10px] font-semibold uppercase tracking-wider text-[#64748B]">
                              {first.name}
                            </div>
                            <div className="text-sm font-semibold text-[#222B45] [overflow-wrap:anywhere]">
                              {formatCell(row.data?.[first.name], first.type)}
                            </div>
                            {row.match_reason && (
                              <div className="mt-1 text-[11px] leading-snug text-[#94A3B8]">{row.match_reason}</div>
                            )}
                          </div>
                          <div className="flex shrink-0">{renderRowActions(row)}</div>
                        </div>
                        <dl className="mt-3 grid grid-cols-1 gap-x-4 gap-y-2 sm:grid-cols-2">
                          {rest.map((c) => (
                            <div key={c.name} className="min-w-0">
                              <dt className="text-[10px] font-semibold uppercase tracking-wider text-[#64748B]">
                                {c.name}
                              </dt>
                              <dd className="text-sm text-[#222B45] [overflow-wrap:anywhere]">
                                {formatCell(row.data?.[c.name], c.type)}
                              </dd>
                            </div>
                          ))}
                        </dl>
                        {hasScores && (
                          <div className="mt-3 flex flex-wrap gap-x-4 gap-y-1 text-xs text-[#64748B]">
                            {SCORE_COLUMNS.map((s) => (
                              <span key={s.key} className="tabular-nums">
                                {s.label} <ScoreValue value={row.scores?.[s.key]} />
                              </span>
                            ))}
                          </div>
                        )}
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>
          </>
        )}
      </div>

      <ViewReportSchedule
        view={view}
        isOpen={reportOpen}
        onClose={() => setReportOpen(false)}
        isSaving={update.isPending}
        onSave={(patch) => update.mutate({ viewId, ...patch })}
        agentSuggestedEmails={agentSuggestedEmails}
      />
      <ViewInstantAlert
        view={view}
        isOpen={alertOpen}
        onClose={() => setAlertOpen(false)}
        isSaving={update.isPending}
        onSave={(alert) => update.mutate({ viewId, instant_alert: alert })}
        suggestedEmails={[...new Set([...(view.report_recipients ?? []), ...agentSuggestedEmails])]}
      />
      {sourceConversationId && (
        <ConversationDetails
          conversationId={sourceConversationId}
          isOpen
          onClose={() => setSourceConversationId(null)}
        />
      )}
    </div>
  );
};
